import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Card } from 'src/common/components/Card';
import { Button } from 'src/common/components/buttons/Button';
import { TextInput } from 'src/common/components/inputs/TextInput/TextInput';
import { GetAdminProjectResponse } from 'src/project/types/projects';
import { useTechCrewsQuery } from 'src/project/queries';
import { unlockWorkdays } from 'src/Conrep/App/api';
import { Loading } from 'src/common/components/Loading';

type Props = {
  project: GetAdminProjectResponse;
  onClose: () => void;
};

export const ProjectEditModalUnlockWorkdays = ({ project, onClose }: Props) => {
  const { t } = useTranslation(['admin', 'common']);
  const { data: techCrews, isPending } = useTechCrewsQuery(project._id);
  const [selectedTechCrew, setSelectedTechCrew] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');

  const handleSave = async () => {
    if (!selectedTechCrew || !startDate) return;

    try {
      await unlockWorkdays(project._id, selectedTechCrew, startDate);
      toast.success(t('admin:projects.modal.unlockWorkdays_updateSuccess'));
      onClose();
    } catch {
      toast.error(t('admin:projects.modal.unlockWorkdays_updateFailure'));
    }
  };

  const isValid = selectedTechCrew !== null && startDate !== '';

  if (isPending) {
    return (
      <div className="flex h-[600px] w-[700px] items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex h-[600px] w-[700px] flex-col justify-between">
      <div className="mt-6 h-full space-y-6 overflow-y-auto bg-gray-100 p-6">
        <div>
          <h3 className="text-lg font-semibold">
            {t('admin:projects.modal.unlockWorkdays_startDate')}
          </h3>
          <Card className="mt-4 p-6">
            <TextInput
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              label={t('admin:projects.modal.unlockWorkdays_startDateLabel')}
            />
          </Card>
        </div>

        <div>
          <h3 className="text-lg font-semibold">
            {t('admin:projects.modal.unlockWorkdays_techCrewList')}
          </h3>
          <Card className="mt-4 p-6">
            <div className="max-h-[300px] space-y-2 overflow-y-auto">
              {techCrews?.map((crew) => (
                <div
                  key={crew._id}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedTechCrew === crew._id}
                  className={`cursor-pointer rounded-md p-3 transition-colors ${
                    selectedTechCrew === crew._id
                      ? 'bg-gray-300'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedTechCrew(crew._id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedTechCrew(crew._id);
                      e.preventDefault();
                    }
                  }}
                >
                  {crew.name}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div className="flex items-center justify-between p-6">
        <div className="flex-1" />
        <div className="flex-1 text-right">
          <Button variant="tertiary" onClick={onClose}>
            {t('common:button.abort')}
          </Button>
          <Button className="ml-2" onClick={handleSave} disabled={!isValid}>
            {t('common:button.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};
